import {type ClassValue, clsx} from 'clsx';
import {twMerge} from 'tailwind-merge';

/** Утилита для объединения классов Tailwind. */
export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

/** Форматирование длительности из секунд в MM:SS (дробные секунды округляются). */
export const formatDuration = (seconds: number): string => {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

/** Длительность в формате MM:SS или H:MM:SS для длинных записей (встречи). */
export const formatDurationLong = (seconds: number): string => {
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

/**
 * Человекочитаемый номер записи (звонок/встреча/задача) для UI и обращений в техподдержку.
 * Бэкенд не выдаёт последовательный номер — `id` это UUID (`_id`). Показываем:
 *  - чистое число, если id вида `C-1023` / `OV-1023` / `1023` (сид/мок) → `1023`;
 *  - первые 8 hex-символов UUID в верхнем регистре (`16EC8F2C`) — короткий
 *    стабильный код (префикс реального `_id`; по нему техподдержка находит запись).
 * Префиксы моков (`C-`, `call-`, `M-`, `meeting-`, `OV-`, `T-`) снимаются.
 */
export const formatCallNumber = (
  rawId: string | number | null | undefined,
): string => {
  if (rawId === null || rawId === undefined || rawId === '') return '—';
  if (typeof rawId === 'number') return String(rawId);
  const id = rawId.replace(/^(C-|call-|M-|meeting-|OV-|T-)/i, '');
  if (/^\d+$/.test(id)) return id;
  return id.split('-')[0].slice(0, 8).toUpperCase();
};

/** Цвет для оценки по 10-балльной шкале. */
export const scoreToColor = (score: number): string => {
  if (score >= 8) return 'var(--score-good)';
  if (score >= 6) return 'var(--score-warn)';
  if (score >= 4) return 'var(--score-default)';
  return 'var(--score-bad)';
};

/** CSS-класс для оценки по 10-балльной шкале: серый=0, красный=1–3, жёлтый=4–6, зелёный=7–10. */
export const scoreToClass = (score: number): string => {
  if (score === 0) return 'text-[var(--color-muted-foreground)]';
  if (score >= 7) return 'text-[var(--score-good)]';
  if (score >= 4) return 'text-[var(--score-warn)]';
  return 'text-[var(--score-bad)]';
};

/** Форматирование даты для отображения (DD.MM.YYYY HH:mm). */
export const formatDateTime = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString('ru-RU', {
    // Фиксируем TZ компании (Москва, +3), иначе время рендерится в TZ браузера
    // (на VMDEV/+5 звонок 17:37 показывался как 19:37).
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Единая «сегодняшняя» дата для mock-данных (ADR-004). */
export const MOCK_TODAY = '2026-05-21';

/**
 * Форматирование дедлайна как relative-строки относительно MOCK_TODAY.
 * Возвращает `{ label, tone }` где tone — для цветового кодирования.
 */
export const formatDueDate = (
  iso: string,
): {label: string; tone: 'overdue' | 'today' | 'soon' | 'later'} => {
  const today = new Date(MOCK_TODAY);
  const due = new Date(iso);
  const days = Math.round((due.getTime() - today.getTime()) / 86400000);

  if (days < 0)
    return {label: `Просрочено на ${Math.abs(days)} дн.`, tone: 'overdue'};
  if (days === 0) return {label: 'Сегодня', tone: 'today'};
  if (days === 1) return {label: 'Завтра', tone: 'soon'};
  if (days <= 7) return {label: `Через ${days} дн.`, tone: 'soon'};
  return {label: due.toLocaleDateString('ru-RU'), tone: 'later'};
};

/**
 * Relative-time строка от ISO-даты до «сейчас».
 *
 * Формат: «N мин назад», «N ч назад», «вчера», «N дн. назад».
 * @param isoDate — ISO-8601 дата события.
 * @param now — опциональная «текущая» дата (для mock: `new Date(MOCK_TODAY)`).
 */
export const formatRelativeTime = (isoDate: string, now?: Date): string => {
  const target = new Date(isoDate);
  const ref = now ?? new Date();
  const diffMs = ref.getTime() - target.getTime();
  const diffMin = Math.floor(diffMs / 60_000);

  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;

  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} ч назад`;

  const diffDays = Math.floor(diffH / 24);
  if (diffDays === 1) return 'вчера';
  if (diffDays < 30) return `${diffDays} дн. назад`;

  return target.toLocaleDateString('ru-RU');
};

/** Русское склонение: plural(3, 'день', 'дня', 'дней') → 'дня'. */
export const plural = (
  n: number,
  one: string,
  few: string,
  many: string,
): string => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
};
